/**
 * FidgetPlugin
 * A simple library to inject interactive fidget toys into the DOM.
 */

class FidgetPlugin {
    constructor() {
        // Styles are now external in fidget.css
    }

    /**
     * Initialize a fidget widget in a container.
     * @param {string} containerId - The ID of the container element.
     * @param {string} type - 'spinner' or 'dice'.
     * @param {object} options - Optional config { imageUrl: '...' }
     */
    init(containerId, type, options = {}) {
        const container = document.getElementById(containerId);
        if (!container) {
            console.error(`FidgetPlugin: Container #${containerId} not found.`);
            return;
        }

        // Clear container
        container.innerHTML = '';
        container.classList.add('fidget-container');

        if (type === 'spinner') {
            container.appendChild(this._createSpinner(options));
        } else if (type === 'dice') {
            container.appendChild(this._createDice(options));
        } else {
            console.error(`FidgetPlugin: Unknown type '${type}'.`);
        }
    }

    _createSpinner(options) {
        const spinner = document.createElement('div');
        spinner.className = 'fidget-spinner';

        // Add behavior classes
        if (options.autoSpin !== false) {
            spinner.classList.add('fidget-spinner--ambient');
        }
        if (options.hoverSpin !== false) {
            spinner.classList.add('fidget-spinner--hover');
        }

        if (options.imageUrl) {
            spinner.classList.add('fidget-spinner--image');

            // Create an image element for better reliability
            const img = document.createElement('img');
            img.src = options.imageUrl;
            img.style.width = '100%';
            img.style.height = '100%';
            img.style.borderRadius = '50%';
            img.style.objectFit = 'cover';

            // Error handling to see if image fails
            img.onerror = function () {
                console.error("FidgetPlugin: Failed to load image at " + options.imageUrl);
                spinner.style.backgroundColor = '#ff5e57'; // Fallback to red so it's not invisible
            };

            spinner.appendChild(img);
        } else {
            // Add 3 arms
            for (let i = 0; i < 3; i++) {
                const arm = document.createElement('div');
                arm.className = 'arm';
                spinner.appendChild(arm);
            }

            // Center
            const center = document.createElement('div');
            center.className = 'center-bearing';
            spinner.appendChild(center);
        }

        // Interaction - only keep click if explicitly enabled or by default if no other interaction
        if (options.clickSpin !== false && !options.autoSpin && !options.hoverSpin) {
            let rotation = 0;
            spinner.addEventListener('click', () => {
                const spin = 720 + Math.random() * 720;
                rotation += spin;
                spinner.style.transform = `rotate(${rotation}deg)`;
            });
        }

        return spinner;
    }

    _createDice(options) {
        const scene = document.createElement('div');
        scene.className = 'fidget-dice-scene';

        const cube = document.createElement('div');
        cube.className = 'fidget-dice';

        // Faces 1-6
        const faces = [
            { class: 'fidget-dice__face--1', text: '1' },
            { class: 'fidget-dice__face--2', text: '2' },
            { class: 'fidget-dice__face--3', text: '3' },
            { class: 'fidget-dice__face--4', text: '4' },
            { class: 'fidget-dice__face--5', text: '5' },
            { class: 'fidget-dice__face--6', text: '6' },
        ];

        faces.forEach(faceData => {
            const face = document.createElement('div');
            face.className = `fidget-dice__face ${faceData.class}`;
            face.textContent = faceData.text; // Simplification: using numbers
            cube.appendChild(face);
        });

        scene.appendChild(cube);

        // Interaction
        cube.addEventListener('click', () => {
            // Roll to random sides
            // Random rotate X and Y in multiples of 90 degrees
            const xRand = Math.floor(Math.random() * 4 + 5) * 90; // at least a few spins
            const yRand = Math.floor(Math.random() * 4 + 5) * 90;

            cube.style.transform = `rotateX(${xRand}deg) rotateY(${yRand}deg)`;
        });

        return scene;
    }
}

// Expose to window
window.FidgetPlugin = new FidgetPlugin();
